// oferta.service.ts
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';

export interface LeadData {
  nome: string;
  nomeDoVendedor: string;
  valorParcela: string;
  modeloCompleto: string;
  versao: string;
  nomeDealer?: string;
  tipoCampanha?: string;
}

// oferta.service.ts
export interface OfertaResponse {
  message: string;
  oferta: string; // Texto da oferta gerado pela API MAIA (pronto para enviar ao cliente)
}

@Injectable({
  providedIn: 'root'
})
export class OfertaService extends BaseApiService {
  private readonly DEFAULT_DEALER = 'VW Original Leste';

  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Gera o texto de uma oferta personalizada via API MAIA
   * A API MAIA processa os dados do lead e retorna um texto de oferta pronto para ser enviado ao cliente
   * @param leadData Dados do lead
   * @param tipoCampanha Tipo de campanha (opcional)
   * @param tipoTom Tipo de tom da oferta (Amigável, Formal, Persuasiva). Padrão: Amigável
   * @returns OfertaResponse com o texto gerado no campo 'oferta'
   */
  enviarOferta(
    leadData: LeadData,
    tipoCampanha?: string,
    tipoTom: string = 'Amigável'
  ): Observable<OfertaResponse> {

    if (!this.validarLeadData(leadData)) {
      throw new Error('Dados do lead incompletos');
    }

    const body = {
      nome: leadData.nome,
      nomeDoVendedor: leadData.nomeDoVendedor,
      valorParcela: leadData.valorParcela,
      modeloCompleto: leadData.modeloCompleto,
      versao: leadData.versao,
      nomeDealer: leadData.nomeDealer || this.DEFAULT_DEALER,
      tipoCampanha: tipoCampanha || leadData.tipoCampanha
    };

    if (this.configService.isDebugEnabled()) {
      console.log('Enviando oferta para backend:', { cliente: leadData.nome, tipoTom });
    }

    // 🔹 Usar método post herdado do BaseApiService
    return this.post<OfertaResponse>(
      `/oferta/enviar?tipoTom=${encodeURIComponent(tipoTom)}`,
      body
    );
  }
  /**
   * Gera texto de oferta com tom amigável
   * @returns Texto personalizado da oferta no campo 'oferta'
   */
  enviarOfertaAmigavel(leadData: LeadData, tipoCampanha?: string): Observable<OfertaResponse> {
    return this.enviarOferta(leadData, tipoCampanha, 'Amigável');
  }

  /**
   * Gera texto de oferta com tom formal
   * @returns Texto personalizado da oferta no campo 'oferta'
   */
  enviarOfertaFormal(leadData: LeadData, tipoCampanha?: string): Observable<OfertaResponse> {
    return this.enviarOferta(leadData, tipoCampanha, 'Formal');
  }

  /**
   * Gera texto de oferta com tom persuasivo
   * @returns Texto personalizado da oferta no campo 'oferta'
   */
  enviarOfertaPersuasiva(leadData: LeadData, tipoCampanha?: string): Observable<OfertaResponse> {
    return this.enviarOferta(leadData, tipoCampanha, 'Persuasiva');
  }

  /**
   * Gera texto de oferta focado em serviços financeiros
   * @returns Texto personalizado da oferta no campo 'oferta'
   */
  enviarOfertaServicosFinanceiros(leadData: LeadData, tipoCampanha?: string): Observable<OfertaResponse> {
    return this.enviarOferta(leadData, tipoCampanha, 'Serviços Financeiros');
  }

  /**
   * Valida dados obrigatórios do lead
   */
  private validarLeadData(leadData: LeadData): boolean {
    return !!(
      leadData.nome &&
      leadData.nomeDoVendedor &&
      leadData.valorParcela &&
      (leadData.modeloCompleto ||
      leadData.versao)
    );
  }
}
